import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useMaintenancesApi } from "../../http/maintenances/api";
import { UpsertMaintenance } from "../../http/maintenances/types";
import { convertLocalDateToUTC, convertMilesToKilometers } from "../../types/utils";
import ModalDialog from "../ui/ModalDialog";
import MaintenanceForm, { MaintenanceFormBody } from "./MaintenanceForm";

type EditMaintenanceDialogProps = {
  maintenanceId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
};

const EditMaintenanceDialog: FC<EditMaintenanceDialogProps> = ({ maintenanceId, onClose, carId }) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const maintenancesApi = useMaintenancesApi();
  const distanceInMiles = !!userSettings.me.distanceInMiles;

  const maintenanceSwr = maintenancesApi.getAdminMaintenance(maintenanceId, aborter.current.signal);
  const [maintenance, isInitialized] = useDataInitializer(maintenanceSwr.data, maintenanceSwr.error);

  useEffect(() => {
    maintenanceId && maintenanceSwr.trigger();
  }, [maintenanceId]);

  useEffect(() => {
    maintenanceSwr.error && dispatch(showErrorNotification(i18n, maintenanceSwr.error));
  }, [maintenanceSwr.error]);

  const onSubmit = async (data: MaintenanceFormBody) => {
    if (maintenance) {
      const distance =
        data.distance != undefined && distanceInMiles ? convertMilesToKilometers(data.distance) : data.distance;

      const body: UpsertMaintenance = {
        carId: carId ?? data.car?.id ?? maintenance.car.id,
        date: convertLocalDateToUTC(data.date),
        typeId: data.type.id,
        description: data.description,
        distance,
        cost: data.cost,
      };

      setIsSubmitting(true);
      try {
        await maintenancesApi.updateAdminMaintenance(maintenance.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={() => onClose()} title={i18n.t.maintenance.edit}>
      <MaintenanceForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.maintenances.update || isSubmitting || !maintenance?.id}
        isLoading={!isInitialized}
        maintenance={maintenance}
        allowSelectCar={!carId}
      />
    </ModalDialog>
  );
};

export default EditMaintenanceDialog;
